// Minimal IndexedDB wrapper. Non-extractable CryptoKeys can be stored here via
// structured clone and reloaded still non-extractable — that's how the private
// keys survive reloads without ever exposing their bytes to app code.

const DB_NAME = 'circle';
const DB_VERSION = 1;

export const STORE_IDENTITY = 'identity';
export const STORE_FRIENDS = 'friends';
export const STORE_CACHE = 'cache'; // cached friend profiles (encrypted-at-rest later)

let dbPromise: Promise<IDBDatabase> | null = null;

function openDb(): Promise<IDBDatabase> {
  if (dbPromise) return dbPromise;
  dbPromise = new Promise((resolve, reject) => {
    const req = indexedDB.open(DB_NAME, DB_VERSION);
    req.onupgradeneeded = () => {
      const db = req.result;
      if (!db.objectStoreNames.contains(STORE_IDENTITY)) db.createObjectStore(STORE_IDENTITY);
      if (!db.objectStoreNames.contains(STORE_FRIENDS)) db.createObjectStore(STORE_FRIENDS);
      if (!db.objectStoreNames.contains(STORE_CACHE)) db.createObjectStore(STORE_CACHE);
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error ?? new Error('IndexedDB open failed'));
  });
  return dbPromise;
}

function tx<T>(store: string, mode: IDBTransactionMode, run: (s: IDBObjectStore) => IDBRequest<T>): Promise<T> {
  return openDb().then(
    (db) =>
      new Promise<T>((resolve, reject) => {
        const t = db.transaction(store, mode);
        const req = run(t.objectStore(store));
        req.onsuccess = () => resolve(req.result);
        req.onerror = () => reject(req.error ?? new Error('IndexedDB request failed'));
      }),
  );
}

export function idbGet<T>(store: string, key: IDBValidKey): Promise<T | undefined> {
  return tx<T | undefined>(store, 'readonly', (s) => s.get(key) as IDBRequest<T | undefined>);
}

export function idbPut(store: string, key: IDBValidKey, value: unknown): Promise<IDBValidKey> {
  return tx<IDBValidKey>(store, 'readwrite', (s) => s.put(value, key));
}

export function idbDelete(store: string, key: IDBValidKey): Promise<undefined> {
  return tx<undefined>(store, 'readwrite', (s) => s.delete(key) as IDBRequest<undefined>);
}

export function idbGetAll<T>(store: string): Promise<T[]> {
  return tx<T[]>(store, 'readonly', (s) => s.getAll() as IDBRequest<T[]>);
}

export async function idbClearAll(): Promise<void> {
  await Promise.all(
    [STORE_IDENTITY, STORE_FRIENDS, STORE_CACHE].map((store) =>
      tx<undefined>(store, 'readwrite', (s) => s.clear() as IDBRequest<undefined>),
    ),
  );
}
